import React from 'react';

import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { ChannelMapEntry, DmxResolution, LinkedZoneDoc } from '@ledium/types';
import { DmxChannelMapper, type DmxChannelMapperRow } from '@ledium/ui-common';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

interface UsedLightChannel {
    id: string;
    name: string;
    dmxAddress: number;
    resolution: DmxResolution;
}

interface ChannelMapEditDialogProps {
    open: boolean;
    link: LinkedZoneDoc;
    usedChannels: UsedLightChannel[];
    onClose: () => void;
    onSaved: () => void;
}

function ChannelMapEditDialog({ open, link, usedChannels, onClose, onSaved }: ChannelMapEditDialogProps) {
    const [value, setValue] = React.useState<Record<string, number | undefined>>(() =>
        Object.fromEntries(link.channelMap.map((e) => [e.sourceChannelId, e.dmxAddress]))
    );
    const [testingChannelId, setTestingChannelId] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [takenAddresses, setTakenAddresses] = React.useState<number[]>([]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const ownAddresses = new Set(link.channelMap.map((e) => e.dmxAddress));
        lediumApi.get<number[]>('lightChannels/occupiedAddresses').then((occupied) => {
            if (occupied) {
                setTakenAddresses(occupied.filter((addr) => !ownAddresses.has(addr)));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const orphanRows: DmxChannelMapperRow[] = link.channelMap
        .filter((e) => !usedChannels.find((c) => c.id === e.sourceChannelId))
        .map((e) => ({
            channelId: e.sourceChannelId,
            name: i18n('linked_project_deleted_channel') as string,
            currentDmxAddress: e.dmxAddress,
            resolution: e.resolution
        }));

    const allRows: DmxChannelMapperRow[] = [
        ...usedChannels.map((c) => ({
            channelId: c.id,
            name: c.name,
            currentDmxAddress: c.dmxAddress,
            resolution: c.resolution
        })),
        ...orphanRows
    ];

    async function handleTest(channelId: string, dmxAddress: number, resolution: number) {
        setTestingChannelId(channelId);
        await lediumApi.get(`lightChannels/testDmx/${dmxAddress}/${resolution}`);
        setTestingChannelId(null);
    }

    async function handleSave() {
        setSaving(true);
        setError(null);
        const channelMap: ChannelMapEntry[] = allRows
            .filter((row) => value[row.channelId] !== undefined)
            .map((row) => ({
                sourceChannelId: row.channelId,
                dmxAddress: value[row.channelId]!,
                resolution: row.resolution as DmxResolution
            }));
        const result = await lediumApi.put<LinkedZoneDoc>(`linkedZones/${link._id}`, { channelMap });
        setSaving(false);
        if (!result) {
            setError(i18n('error_loading_data') as string);
            return;
        }
        onSaved();
        onClose();
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{i18n('linked_project_edit_mapping')}</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <DmxChannelMapper
                    channels={allRows}
                    value={value}
                    onChange={setValue}
                    onTest={handleTest}
                    testingChannelId={testingChannelId}
                    takenAddresses={takenAddresses}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {i18n('cancel')}
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : undefined}>
                    {i18n('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ChannelMapEditDialog;
